import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useOpenPositionBranchAggregateSearchQuery } from "@/common/api/open-position/use-open-position-branch-aggregate-search.query";
import type {
  OpenPositionAggregateSearchBody,
  OpenPositionAggregateSearchParameters,
} from "@/common/api/open-position/use-open-position-customer-aggregate-search.query";
import { createBasePredefinedTable } from "@/components/data-table/predefined/_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  BaseSorting,
  DataFetchHooks,
  PredefinedTableConfig,
} from "@/components/data-table/predefined/_components/base-predefined-table.types";
import { TableFeatures } from "@/components/data-table/types";
import { CompanyFilter } from "@/components/filter/predefined/company-filter";
import { CompanyGroupFilter } from "@/components/filter/predefined/company-group-filter";
import i18next from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useOpenPositionSearchBody } from "../_hooks/use-open-position-search-body";
import { AggregateSearchHookArgs, useAggregateTableQuery } from "./_hooks/use-aggregate-table-query";
import { BranchAggregateRow, branchAggregateColumns, toBranchAggregateRow } from "./branch-aggregate-columns";

type BranchAggregateSorting = BaseSorting;

function useBranchAggregateTableQuery(args?: AggregateSearchHookArgs) {
  return useAggregateTableQuery(useOpenPositionBranchAggregateSearchQuery, toBranchAggregateRow, args);
}

const DEFAULT_COLUMNS = [
  branchAggregateColumns.name,
  branchAggregateColumns.invoiceBalance,
  branchAggregateColumns.paymentBalance,
  branchAggregateColumns.totalBalance,
  branchAggregateColumns.positionCount,
  branchAggregateColumns.overdueAmount,
];

const TABLE_CONFIG: PredefinedTableConfig<BranchAggregateSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [{ field: "totalBalance", direction: "desc" }],
  defaultTableConfig: {
    searchPlaceholder: i18next.t("fia:SearchBranches"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  },
  defaultRowActions: {
    showEdit: false,
    showView: false,
    showDelete: false,
    showRestore: false,
  },
  sortingMapping: {
    name: ["name"],
    invoiceBalance: ["invoiceBalance"],
    paymentBalance: ["paymentBalance"],
    totalBalance: ["totalBalance"],
    positionCount: ["positionCount"],
    overdueAmount: ["overdueAmount"],
  },
};

const QUERY_HOOKS: DataFetchHooks<
  BranchAggregateRow,
  OpenPositionAggregateSearchParameters,
  OpenPositionAggregateSearchBody
> = {
  useSearchQuery: useBranchAggregateTableQuery,
};

const BaseBranchAggregateTable = createBasePredefinedTable({
  config: TABLE_CONFIG,
  queryHooks: QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function BranchAggregateTable(
  props: BasePredefinedTableProps<
    BranchAggregateRow,
    BranchAggregateSorting,
    OpenPositionAggregateSearchParameters,
    OpenPositionAggregateSearchBody
  >,
) {
  const { t } = useTranslation(["fia"]);
  const { name = "branch-aggregate-table", filters, body, ...restProps } = props;

  const bodyWithReferenceDate = useOpenPositionSearchBody(body);

  const defaultFilters = useMemo(
    () => filters ?? [new CompanyGroupFilter({ id: "companyGroup" }), new CompanyFilter({ id: "company" })],
    [filters],
  );

  return (
    <BaseBranchAggregateTable
      name={name}
      searchPlaceholder={t("fia:SearchBranches")}
      filters={defaultFilters}
      body={bodyWithReferenceDate}
      onRowNavigate={(row) => (row.branch ? `~/masterdata/branch/${row.branch.uuid}` : undefined)}
      {...restProps}
    />
  );
}
